/**
 * useEntityEnrichment — background entity enrichment lifecycle hook.
 *
 * After transaction expansion, identifies canvas nodes with no entity attribution
 * and kicks off a background enrichment job (TronScan, GoPlus, Etherscan labels).
 * Polls for results and calls back with updates so canvas labels update live.
 *
 * Usage:
 *   const { enrichmentStatus, triggerEnrichment } = useEntityEnrichment(onEntityEnriched);
 *
 *   // After adding nodes:
 *   triggerEnrichment(newNodes.map(n => ({ address: n.data.id, chain })));
 */

import { useCallback, useRef, useState } from 'react';
import { entityEnrichmentService } from '../services/api';
import type { EnrichmentStatus } from '../components/NodeInspector/TxLoadBar';

const POLL_INTERVAL_MS = 2500;
const MAX_POLLS = 60; // 60 * 2.5s = 2.5 min max

interface EnrichableAddress {
    address: string;
    chain: string;
}

interface EntityUpdate {
    address: string;
    entityName: string;
    entityType?: string;
}

export function useEntityEnrichment(
    onEntityEnriched?: (address: string, entityName: string, entityType?: string) => void,
) {
    const [enrichmentStatus, setEnrichmentStatus] = useState<EnrichmentStatus>({
        status: 'idle',
        total: 0,
        found: 0,
        currentProvider: null,
    });

    // Track active job to prevent duplicate polls
    const activeJobRef = useRef<string | null>(null);
    const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pollCountRef = useRef(0);

    const stopPolling = useCallback(() => {
        if (pollTimerRef.current) {
            clearTimeout(pollTimerRef.current);
            pollTimerRef.current = null;
        }
        activeJobRef.current = null;
        pollCountRef.current = 0;
    }, []);

    const pollJob = useCallback(async (jobId: string) => {
        if (activeJobRef.current !== jobId) return; // superseded by newer job
        pollCountRef.current += 1;

        try {
            const status = await entityEnrichmentService.getStatus(jobId);

            if (activeJobRef.current !== jobId) return; // superseded while awaiting

            setEnrichmentStatus({
                status: status.status,
                total: status.total,
                found: status.found,
                currentProvider: status.current_provider,
            });

            // Fire callbacks for each newly-identified entity
            if (status.results && onEntityEnriched) {
                for (const [address, entity] of Object.entries(status.results)) {
                    if (entity.name) {
                        onEntityEnriched(address, entity.name, entity.category);
                    }
                }
            }

            if (status.status === 'complete' || status.status === 'error') {
                stopPolling();
                return;
            }

            if (pollCountRef.current >= MAX_POLLS) {
                stopPolling();
                setEnrichmentStatus(prev => ({ ...prev, status: 'complete' }));
                return;
            }

            // Schedule next poll
            pollTimerRef.current = setTimeout(() => pollJob(jobId), POLL_INTERVAL_MS);
        } catch {
            stopPolling();
            setEnrichmentStatus(prev => ({ ...prev, status: 'error', message: 'Enrichment poll failed' }));
        }
    }, [onEntityEnriched, stopPolling]);

    const triggerEnrichment = useCallback(async (addresses: EnrichableAddress[]) => {
        // Only enrich addresses that look "unknown" (no enrichment benefit from those
        // already identified in the fast sync batch). The backend filters further.
        if (addresses.length === 0) return;

        // Cancel any in-flight job
        stopPolling();
        setEnrichmentStatus({ status: 'running', total: addresses.length, found: 0, currentProvider: null });

        try {
            const job = await entityEnrichmentService.startEnrichment(addresses);
            activeJobRef.current = job.job_id;
            pollCountRef.current = 0;

            // Start polling after a short delay to let the backend warm up
            pollTimerRef.current = setTimeout(() => pollJob(job.job_id), 1000);
        } catch {
            setEnrichmentStatus({ status: 'error', total: addresses.length, found: 0, currentProvider: null });
        }
    }, [stopPolling, pollJob]);

    // Expose stopPolling so callers can tie enrichment lifecycle to other
    // long-running flows (e.g. when an investigation completes or is reset,
    // we want to stop polling for entity labels of nodes the user may not
    // even be looking at anymore — that's a B1 cause of late canvas writes).
    const cancelEnrichment = useCallback(() => {
        stopPolling();
        setEnrichmentStatus(prev => (
            prev.status === 'running'
                ? { ...prev, status: 'complete' }
                : prev
        ));
    }, [stopPolling]);

    return { enrichmentStatus, triggerEnrichment, cancelEnrichment };
}
